import type { ConversationSummary, InboxCounts, InboxFilter, InboxMessage } from '@dnx/contracts';
import { queryOptions } from '@tanstack/react-query';
import { api } from '../lib/api.js';

export const inboxKeys = {
  all: ['inbox'] as const,
  list: (f: InboxFilter) => ['inbox', 'list', f] as const,
  counts: ['inbox', 'counts'] as const,
  conversation: (id: string) => ['inbox', 'conversation', id] as const,
  messages: (id: string) => ['inbox', 'messages', id] as const,
};

export const listQuery = (f: InboxFilter) => queryOptions({
  queryKey: inboxKeys.list(f), queryFn: () => api<ConversationSummary[]>(`/inbox/conversations?filtro=${f}`), staleTime: 30_000,
});
export const countsQuery = queryOptions({
  queryKey: inboxKeys.counts, queryFn: () => api<InboxCounts>('/inbox/counts'), staleTime: 30_000,
});
export const conversationQuery = (id: string) => queryOptions({
  queryKey: inboxKeys.conversation(id), queryFn: () => api<ConversationSummary>(`/inbox/conversations/${id}`), staleTime: 30_000,
});
export const messagesQuery = (id: string) => queryOptions({
  queryKey: inboxKeys.messages(id), queryFn: () => api<InboxMessage[]>(`/inbox/conversations/${id}/messages`), staleTime: 30_000,
});

/** "agora", "há 6 min", "há 2 h", "ontem", "12/10". */
export function relTime(iso: string, now = Date.now()): string {
  const d = new Date(iso);
  const min = Math.floor((now - d.getTime()) / 60_000);
  if (min < 1) return 'agora';
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h} h`;
  const yesterday = new Date(now - 86_400_000);
  if (d.toDateString() === yesterday.toDateString()) return 'ontem';
  return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', timeZone: 'America/Sao_Paulo' }).format(d);
}

/** SLA de primeira resposta (docs/01 §11.4): 5 min atenção, 15 min estourado. */
export function waitTone(waitingSince: string | null, now = Date.now()): 'ok' | 'warn' | 'late' | null {
  if (!waitingSince) return null;
  const min = (now - new Date(waitingSince).getTime()) / 60_000;
  return min >= 15 ? 'late' : min >= 5 ? 'warn' : 'ok';
}

export const clock = (iso: string) =>
  new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' }).format(new Date(iso));
